import type { ChatAppResponse } from "../../api/models";
import { erholCitationKind } from "../LiveCitation/liveCitationModel";

type HtmlParsedAnswer = {
    answerHtml: string;
    citations: string[];
};

const citationHrefPattern = /^#answer-citation-([1-9]\d*)$/;

export function citationFromHref(href: string | undefined, citations: readonly string[]): string | undefined {
    const match = href && citationHrefPattern.exec(href);
    return match ? citations[Number(match[1]) - 1] : undefined;
}

function isCitationValid(contextDataPoints: unknown, citationCandidate: string): boolean {
    if (!/.+\.\w{1,}(?:#\S*)?$/.test(citationCandidate)) return false;

    const dataPoints = Array.isArray(contextDataPoints)
        ? contextDataPoints
        : contextDataPoints && typeof contextDataPoints === "object" && "text" in contextDataPoints
          ? contextDataPoints.text
          : undefined;

    return Array.isArray(dataPoints) && dataPoints.some(
        (dataPoint: unknown) => typeof dataPoint === "string" && dataPoint.startsWith(citationCandidate)
    );
}

export function parseAnswerToHtml(answer: ChatAppResponse, isStreaming: boolean): HtmlParsedAnswer {
    const contextDataPoints = answer.context?.data_points ?? [];
    const citations: string[] = [];
    const messageContent = typeof answer.message.content === "string"
        ? answer.message.content
        : JSON.stringify(answer.message.content);
    let parsedAnswer = messageContent.trim();

    // Omit a citation that is still being typed during streaming.
    if (isStreaming) {
        let lastIndex = parsedAnswer.length;
        for (let index = parsedAnswer.length - 1; index >= 0; index--) {
            if (parsedAnswer[index] === "]") break;
            if (parsedAnswer[index] === "[") {
                lastIndex = index;
                break;
            }
        }
        parsedAnswer = parsedAnswer.substring(0, lastIndex);
    }

    const parts = parsedAnswer.split(/\[([^\]]+)\]/g);
    const validCitations = parts.filter((part, index) => index % 2 === 1 && isCitationValid(contextDataPoints, part));
    const uniqueCitations = new Set(validCitations);
    // A single aggregate statistics result already has a named source in the answer footer.
    const hideInlineStatisticsCitation = uniqueCitations.size === 1
        && erholCitationKind(validCitations[0]) === "workflowStatistics";

    const fragments = parts.map((part, index) => {
        if (index % 2 === 0) return part;
        if (!isCitationValid(contextDataPoints, part)) return `[${part}]`;

        let citationIndex = citations.indexOf(part) + 1;
        if (citationIndex === 0) {
            citations.push(part);
            citationIndex = citations.length;
        }

        if (hideInlineStatisticsCitation) return "";
        return `<a class="supContainer" href="#answer-citation-${citationIndex}"><sup>${citationIndex}</sup></a>`;
    });

    return { answerHtml: fragments.join(""), citations };
}
